import { useQuery } from '@tanstack/react-query';
import { useClientStore } from '@/client/client-store';
import { ensureRoleScreens } from '@/auth/role-screen-access';
import { ensureRawTiles } from '@/apps/use-app-tiles';

/**
 * Raw tile DTO shape from the GetTiles endpoint — same as in `use-app-tiles.ts`.
 * Only the fields needed for header icon gating are listed.
 */
interface AppTileDto {
  tileRoutePath: string;
  appTypeID: number;
}

/**
 * Angular `HeaderAppsEnums` — the `tileRoutePath` values for header-only icons.
 * Source: `raptor-shell-header/src/app/core/enum/header-apps.ts`.
 */
const HeaderAppsRoutes = {
  OrchestratorIcon: 'popup:apps/ORCHESTRATORUITASKS',
  AppsIcon: 'popup:apps/AppsMenu',
  GroupsIcon: 'popup:apps/MyTeams',
  HelpIcon: 'popup:apps/STF',
  ChatBot: 'popup:apps/ChatBot',
  MCPChatbot: 'popup:apps/MCPChatBot',
} as const;

/** Which header buttons are visible for the current client + role. */
export interface HeaderIconVisibility {
  showOrchestrator: boolean;
  showApps: boolean;
  showGroups: boolean;
  showHelp: boolean;
  showChatBot: boolean;
  showEyqfs: boolean;
  /** 'chatbot' = traditional inline, 'mcpChatbot' = popup MFE */
  chatbotType: 'chatbot' | 'mcpChatbot' | '';
}

const EMPTY_VISIBILITY: HeaderIconVisibility = {
  showOrchestrator: false,
  showApps: false,
  showGroups: false,
  showHelp: false,
  showChatBot: false,
  showEyqfs: false,
  chatbotType: '',
};

/**
 * Derive header icon visibility from the RAW tile list for a client.
 *
 * This mirrors Angular's `AppComponent.getWorkspacetile()` logic
 * (`raptor-shell-header/src/app/app.component.ts:349-383`):
 *   1. Fetch ALL tiles for the selected client (same endpoint `use-app-tiles`
 *      uses: `GET /portalapps/api/v1/Apps/GetTiles`).
 *   2. Iterate tiles and match `tileRoutePath` to `HeaderAppsEnums` values.
 *   3. Set the corresponding boolean flags.
 *
 * The existing `useAppTiles` hook filters OUT `PortalHeader` / `ChatBot` / `SHD`
 * tile types for the *launcher*. This hook needs the UNFILTERED list so it can
 * detect header-only tiles like ChatBot and MCPChatbot.
 */
function deriveVisibility(tiles: AppTileDto[]): HeaderIconVisibility {
  const vis: HeaderIconVisibility = { ...EMPTY_VISIBILITY };

  for (const tile of tiles) {
    switch (tile.tileRoutePath) {
      case HeaderAppsRoutes.OrchestratorIcon:
        vis.showOrchestrator = true;
        break;
      case HeaderAppsRoutes.AppsIcon:
        vis.showApps = true;
        break;
      case HeaderAppsRoutes.GroupsIcon:
        vis.showGroups = true;
        break;
      case HeaderAppsRoutes.HelpIcon:
        vis.showHelp = true;
        break;
      case HeaderAppsRoutes.ChatBot:
        vis.chatbotType = 'chatbot';
        vis.showChatBot = true;
        break;
      case HeaderAppsRoutes.MCPChatbot:
        vis.chatbotType = 'mcpChatbot';
        vis.showEyqfs = true;
        break;
    }
  }

  return vis;
}

/**
 * React hook that provides role/client-gated header icon visibility.
 *
 * Re-fetches automatically when the selected client changes. While loading
 * (or on error) all icons are hidden — matching Angular's behavior where
 * `HeaderIcons` starts with all flags `false` and `resetHeaderIcons()` clears
 * them before each refetch.
 */
export function useHeaderIconVisibility(): HeaderIconVisibility {
  const clientId = useClientStore((s) => s.current?.id);

  const { data } = useQuery({
    queryKey: ['header-icon-tiles', clientId],
    queryFn: async () => {
      if (!clientId) return EMPTY_VISIBILITY;
      // Share the launcher's deduped GetTiles fetch — one HTTP call serves the
      // header icons, the app launcher, and the route-loader registry warm-up.
      const tiles = await ensureRawTiles(clientId);

      // Use the shared role-screen cache (ensureRoleScreens) instead of a
      // separate http() call — eliminates the duplicate GetRolesMapping request.
      let allowedScreens: Set<string>;
      try {
        const screens = await ensureRoleScreens(clientId);
        allowedScreens = new Set(screens);
      } catch {
        return EMPTY_VISIBILITY;
      }

      const filtered =
        allowedScreens.size > 0 ? tiles.filter((t) => allowedScreens.has(t.tileRoutePath)) : tiles;

      return deriveVisibility(filtered);
    },
    enabled: !!clientId,
    staleTime: 5 * 60_000,
    retry: 1,
  });

  return data ?? EMPTY_VISIBILITY;
}
